'use client';

import type { Address, ChainId } from '@morpho-org/blue-sdk';
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { safeParseNumber } from '@morpho-org/blue-sdk-viem';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { previewVaultV2InKindRedeem } from '@morpho-org/morpho-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { Avatar, type DialogOptions, InfoTooltip } from '@morpho-org/ui-v6/client';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { Card, CardText, ExternalLink, Skeleton, Tag } from '@morpho-org/ui-v6/server';
import { cn, formatters, getMarketName, getMarketNameParts } from '@morpho-org/ui-v6/utils';
import { Button, TruncatedTooltip } from '@repo/ui';
import { ArrowRight, Close } from '@repo/ui/icons';
import { skipToken, useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { InKindRedemptionEntrySource } from '@/lib/analytics/client/events';
import { ErrorServiceUnavailable } from '@/components/common/Error/ErrorServiceUnavailable';
import InKindRedemptionPositionExplanation, { WALLET_LEG_CEILING_NOTE, WALLET_LEG_NOTE } from '@/components/common/InKindRedemptionPositionExplanation';
import SectionErrorBoundary from '@/components/common/SectionErrorBoundary';
import { IN_KIND_REDEMPTION_HELP_URL } from '@/config/external-urls';
import { useWeb3Context } from '@/contexts/Web3Context';
import { formatTokenAmount } from '@/helpers/formatters';
import useCloseDialogOnWalletChange from '@/hooks/useCloseDialogOnWalletChange';
import useFocusOnMount from '@/hooks/useFocusOnMount';
import { orpc } from '@/lib/orpc/client';
import { MAX_MARKET_KEYS } from '@/lib/procedures/marketList.contract';
import type { InKindRedemptionPreview } from '../VaultV2ReviewDialog/inKindRedemption.helpers';
import { useVaultV2 } from '../../../hooks/useVaultV2';
import { useVaultV2Position } from '../../../hooks/useVaultV2Position';
import InfoCallout from '../InfoCallout';
import { VaultV2InKindRedemptionReviewDialog } from '../VaultV2ReviewDialog';
import { capExitToMarketCapacity, capInKindExitToSharesWithFee } from '../VaultV2ReviewDialog/deallocation';
import { type InKindRedemptionCapacity, inKindRedemptionFeeAssets, inKindRedemptionMarketCapacity, inKindRedemptionPenaltyRate, toSafeInKindPreview } from '../VaultV2ReviewDialog/inKindRedemption.helpers';
const MARKET_APY_STALE_TIME_MS = 10_000;

// Literal class strings: Tailwind won't emit template-built grid utilities.
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[minmax(0,1fr)_150px_80px] items-center gap-xs';
const previewKey = (preview: InKindRedemptionPreview) => preview.marketParams.id;
interface VaultV2InKindRedemptionDialogProps {
  source: InKindRedemptionEntrySource;
  vaultAddress: Address;
  chainId: ChainId;
  requestedExitAssets: bigint;
  userPosition: bigint;
  vaultAsset: InterfaceToken | null | undefined;
  dialogOptions?: DialogOptions;
  onTxSuccess?: () => void;
}
interface DialogRow {
  preview: InKindRedemptionPreview;
  /** What this holder can place in this Market, penalty included — the `reachableAssets` of
   * `inKindRedemptionMarketCapacity`. */
  capacityAssets: bigint;
  collateralAsset: InterfaceToken | null;
  loanAsset: InterfaceToken;
  supplyApy: bigint | undefined;
  /** Why the row cannot be picked, or `undefined` when it can. */
  disabledReason: string | undefined;
}

/**
 * Why a row is offered but cannot be picked. The clamp reserves shares for the penalty and for
 * price drift, and on a small enough position that reservation is the whole balance — no amount
 * the user types changes it, so the row has to say so rather than fail at Review.
 */
const POSITION_TOO_SMALL_REASON = 'Your position is too small to redeem into this Market once the redemption fee is taken.';

/**
 * The other dead end, and a property of the Market rather than the holder: the allocation, held
 * under the capacity margin, leaves nothing to fund, and the SDK rejects an exit with no
 * deallocation. Another Market is genuinely worth trying — which is why the holder's own limit
 * must not borrow this string.
 */
const NO_MARKET_ROOM_REASON = 'This Market has no room left to receive a redemption right now.';
const isRowDisabled = (row: DialogRow | undefined) => isDefined(row?.disabledReason);
const VaultV2InKindRedemptionDialog = ({
  source,
  vaultAddress,
  chainId,
  requestedExitAssets,
  userPosition,
  vaultAsset,
  dialogOptions,
  onTxSuccess
}: VaultV2InKindRedemptionDialogProps) => {
  const {
    toggleDialog
  } = useDialogContext();
  const {
    targetChainId
  } = useWeb3Context();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectionAnnouncement, setSelectionAnnouncement] = useState<string | null>(null);
  useCloseDialogOnWalletChange();
  const containerRef = useFocusOnMount<HTMLDivElement>();
  const {
    vaultData,
    isLoading: isVaultLoading,
    error: vaultError
  } = useVaultV2(vaultAddress);
  const {
    userVaultShares,
    isLoading: isPositionLoading,
    error: positionError
  } = useVaultV2Position({
    vaultAddress,
    chainId
  });
  const penalty = isDefined(vaultData) ? inKindRedemptionPenaltyRate(vaultData) : 0n;

  // Accrue to `vaultData.lastUpdate` (block-pinned), not wall clock — otherwise Anvil
  // forks over-accrue and the UI offers a ceiling the tx cannot service.
  const {
    previews,
    previewError
  } = useMemo(() => {
    if (!isDefined(vaultData)) return {
      previews: [],
      previewError: undefined
    };
    // The SDK preview throws documented validation errors (a dust exit rounding to no
    // deallocation, a refresh revealing the Vault no longer has exactly one supported
    // adapter). This memo runs while the chooser itself renders, so the descendant
    // SectionErrorBoundary can never catch a throw from here — catch it and let the
    // snapshot-error branch below present it instead of bubbling to an outer boundary.
    try {
      return {
        previews: buildPreviews(),
        previewError: undefined
      };
    } catch (error) {
      return {
        previews: [],
        previewError: error
      };
    }
    function buildPreviews() {
      if (!isDefined(vaultData)) return [];
      const rawPreviews = previewVaultV2InKindRedeem(vaultData, {
        requestedExitAssets,
        timestamp: vaultData.lastUpdate
      });
      // The bundle exits `capInKindExitToShares`, not the raw preview — restate every row
      // against it here, one screen before the review does the same, so the two agree. The
      // clamp needs the row's own `feeAssets`, which only exists once the preview has run:
      // clamping `requestedExitAssets` up front would be circular.
      if (!isDefined(userVaultShares)) return rawPreviews;
      return rawPreviews.map(preview => {
        // Capacity first, shares second: the two clamps answer different hazards and the share
        // clamp has to see the amount the Market will actually be asked for. The capacity margin
        // is a genuine shortfall against the Market's ceiling, so it flows into
        // `remainingExitAssets` and makes the partial-redemption callout name it — unlike the
        // share clamp's residual, which `toSafeInKindPreview` deliberately leaves out of that
        // field because it is a drift reserve rather than a capacity limit.
        const capacityCapped = capExitToMarketCapacity(preview);
        const shareCapped = capInKindExitToSharesWithFee({
          exitAssets: capacityCapped,
          userVaultShares,
          vault: vaultData,
          feeOf: assets => inKindRedemptionFeeAssets(vaultData, assets)
        });
        return toSafeInKindPreview({
          ...preview,
          remainingExitAssets: requestedExitAssets - capacityCapped
        }, shareCapped, penalty);
      });
    }
  }, [vaultData, requestedExitAssets, userVaultShares, penalty]);

  // The share balance, the vault snapshot and the preview fail the same way for the same
  // reasons: without them the table is empty or uncapped, and a stale cached balance leaves
  // stale caps selectable — so all three errors gate the table and Review below.
  const snapshotError = isDefined(vaultError) ? vaultError : isDefined(positionError) ? positionError : previewError;
  const marketIds = useMemo(() => previews.map(preview_0 => preview_0.marketParams.id), [previews]);

  // Bounded to what the table can render (and what `market.byKeys` takes). `previews` is
  // sorted by allocation descending, so the ones kept are the ones with the most room to redeem
  // into. Both the markets query and the per-market APY fan-out use this same slice, so the
  // positional alignment with `previews` is preserved.
  const boundedMarketIds = useMemo(() => marketIds.slice(0, MAX_MARKET_KEYS), [marketIds]);

  // A Vault can allocate to unlisted Markets, and those are exactly as redeemable as the
  // rest, so `market.byKeys` returns them too.
  // Sorted before it reaches the key: `boundedMarketIds` is in display order, and accrual
  // reordering two similar Markets would otherwise re-key the query, blank the table
  // and trip the "selected Market is no longer available" recovery with nothing unavailable.
  const sortedMarketIds = useMemo(() => boundedMarketIds.toSorted(), [boundedMarketIds]);
  const {
    data: marketsData,
    isLoading: isMarketsLoading,
    error: marketsError
  } = useQuery(orpc.market.byKeys.queryOptions({
    input: sortedMarketIds.length > 0 ? {
      chainId: targetChainId,
      marketIds: sortedMarketIds
    } : skipToken,
    staleTime: Infinity
  }));
  // Rows the bridge could not read are dropped, so a partial list can hide redemption targets.
  // The bridge also leaves out Markets it does not list at all (idle Markets), without flagging
  // `partial`: a requested id missing from the answer hides a target the same way.
  const isMarketsPartial = useMemo(() => {
    if (!isDefined(marketsData)) return false;
    if (marketsData.partial) return true;
    const returnedIds = new Set(marketsData.items.map(market => market.marketId.toLowerCase()));
    return sortedMarketIds.some(marketId => !returnedIds.has(marketId.toLowerCase()));
  }, [marketsData, sortedMarketIds]);

  // Assets come from `market.byKeys` once; the spot APY fans out one request per Market. It only
  // feeds the APY column, which does not move on a dialog's timescale — polling it cost a request
  // per Market every 10s for as long as the dialog stayed open. Capacity and the wallet/Market
  // split come from the local SDK preview.
  const {
    data: marketApys
  } = useQueries({
    queries: boundedMarketIds.map(marketId_0 => orpc.market.apy.queryOptions({
      input: {
        chainId: targetChainId,
        marketId: marketId_0
      },
      staleTime: MARKET_APY_STALE_TIME_MS
    })),
    combine: queries => ({
      data: queries.map(query => query.data)
    })
  });

  // Measured at the Market's ceiling rather than against a typed amount, so the figure holds
  // before anything is entered. The fallback only narrows the type: `previews` is empty without
  // the snapshot, so no row exists to price.
  const rowCapacity = useCallback((preview_1: InKindRedemptionPreview): InKindRedemptionCapacity => isDefined(vaultData) ? inKindRedemptionMarketCapacity(vaultData, preview_1.maxExitAssets, userVaultShares) : {
    marketAssets: ZERO,
    reachableAssets: ZERO
  }, [vaultData, userVaultShares]);
  const rows: DialogRow[] = useMemo(() => {
    const marketsById = new Map((marketsData?.items ?? []).map(market_0 => [market_0.marketId.toLowerCase(), market_0]));
    // `marketApys` is positionally aligned with `previews` (both derive from `marketIds`).
    return previews.flatMap((preview_2, index) => {
      const market_1 = marketsById.get(preview_2.marketParams.id.toLowerCase());
      if (!market_1) return [];
      const apy = marketApys[index]?.supply.netApy;
      const toMarketAsset = ({
        logoUri,
        name,
        ...asset
      }: typeof market_1.loanAsset) => parseApiAsset({
        ...asset,
        name: name ?? undefined,
        logoURI: logoUri,
        chain: {
          id: market_1.chainId
        }
      });
      const capacity = rowCapacity(preview_2);
      return [{
        preview: preview_2,
        capacityAssets: capacity.reachableAssets,
        collateralAsset: market_1.collateralAsset ? toMarketAsset(market_1.collateralAsset) : null,
        loanAsset: toMarketAsset(market_1.loanAsset),
        supplyApy: isDefined(apy) ? safeParseNumber(apy) : undefined,
        // The Market's own limit first, the holder's second: both surface as a zero here, and a
        // row that blames the Market for the holder's balance sends them to another Market that
        // is no better. The raw preview is still positive at all three.
        disabledReason: capacity.marketAssets <= ZERO ? NO_MARKET_ROOM_REASON : preview_2.exitAssets <= ZERO || capacity.reachableAssets <= ZERO ? POSITION_TOO_SMALL_REASON : undefined
      }];
    });
  }, [previews, marketsData, marketApys, rowCapacity]);

  // Ref (not derived): the selection must survive each poll rather than be recomputed from it.
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    const [onlyRow] = rows;
    if (hasAutoSelected.current || isMarketsPartial || rows.length !== 1 || !onlyRow || isRowDisabled(onlyRow)) return;
    hasAutoSelected.current = true;
    setSelectedKey(previewKey(onlyRow.preview));
  }, [rows, isMarketsPartial]);

  // A row that a later poll disabled must stop counting as selected, or Review would hand the
  // builder a zero exit. Dropping it here routes it through the stale-selection recovery below.
  const selectedEntry = rows.find(row => previewKey(row.preview) === selectedKey);
  const selectedRow = isRowDisabled(selectedEntry) ? undefined : selectedEntry;
  const selectedMarketName = selectedRow && getMarketName({
    collateralAsset: selectedRow.collateralAsset,
    loanAsset: selectedRow.loanAsset,
    maxLength: 20
  });
  useEffect(() => {
    if (selectedKey === null || selectedRow) return;
    // A vault poll that re-keys the market-list query empties `rows` while the new list loads
    // (no placeholder data, deliberately) — that gap is not evidence the selection is gone.
    // Wait for the query to settle before declaring the Market unavailable.
    if (isMarketsLoading || marketsData === undefined) return;
    setSelectedKey(null);
    setSelectionAnnouncement(selectedEntry?.disabledReason ? `${selectedEntry.disabledReason} Choose another market.` : 'The selected market is no longer available. Choose another market.');
    containerRef.current?.focus();
  }, [selectedKey, selectedRow, selectedEntry, containerRef, isMarketsLoading, marketsData]);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Arrow keys move the selection *and* the focus, as a radiogroup expects.
  const selectRowAt = (index_0: number) => {
    const target = rows[index_0];
    if (!target || isRowDisabled(target)) return;
    setSelectedKey(previewKey(target.preview));
    rowRefs.current[index_0]?.focus();
  };
  // Arrow keys step over disabled rows, the way a radiogroup steps over disabled radios.
  const selectRowFrom = (from: number, step: number) => {
    for (let offset = 1; offset <= rows.length; offset++) {
      const index_1 = ((from + step * offset) % rows.length + rows.length) % rows.length;
      if (!isRowDisabled(rows[index_1])) return selectRowAt(index_1);
    }
  };
  const tabbableIndex = selectedRow ? rows.indexOf(selectedRow) : Math.max(0, rows.findIndex(row_0 => !isRowDisabled(row_0)));
  const effectiveExitAssets = selectedRow?.preview.exitAssets ?? requestedExitAssets;
  const remainderStayingInVault = selectedRow?.preview.remainingExitAssets ?? ZERO;

  // Idle is served first and penalty-free, and is the same for every market at a given exit
  // size, so the first preview answers for all of them before a row is picked.
  const activePreview = selectedRow?.preview ?? previews[0];
  const walletAssets = activePreview?.idleAssets ?? ZERO;
  // What actually lands in the Market, net of the penalty — the same figure the breakdown row
  // below shows. Quoting the gross leg here put two numbers for one quantity on one screen.
  const inKindPositionAssets = activePreview?.netAssets ?? ZERO;

  // Every choice leaves something behind, so the requested amount is unreachable in one
  // Market. Said up front, before a selection turns it into concrete numbers.
  const noMarketTakesFullAmount = rows.length > 0 &&
  // Every row disabled is a different story — one each row already tells — and this callout
  // would promise a choice ("the Market you choose takes what it can") that cannot be made.
  rows.some(row_2 => !isRowDisabled(row_2)) && rows.every(row_1 => row_1.preview.remainingExitAssets > ZERO);

  // The share balance gates the table: without it neither clamp runs (see `buildPreviews`), so
  // rows would quote raw previews and the position would read above the capacity funding it. Its
  // `isLoading`, not a missing balance — a disabled query would hold the table open forever.
  const isLoading = isVaultLoading || isPositionLoading || isMarketsLoading && rows.length === 0;
  const formatAmount = (amount: bigint) => formatTokenAmount(vaultAsset, amount);
  const openReview = () => {
    if (!selectedRow || !isDefined(vaultData)) return;
    toggleDialog(<VaultV2InKindRedemptionReviewDialog source={source} vaultAddress={vaultAddress} chainId={chainId} preview={selectedRow.preview}
    // Frozen with the preview so the review and confirm screens repeat the
    // same worst-case numbers regardless of later polls.
    penalty={penalty} userPosition={userPosition} onTxSuccess={onTxSuccess} market={{
      collateralAsset: selectedRow.collateralAsset,
      loanAsset: selectedRow.loanAsset
    }} dialogOptions={dialogOptions} />, {
      ...dialogOptions,
      ariaLabel: 'Review in-kind redemption'
    });
  };
  return <div ref={containerRef} tabIndex={-1} className="gap-md flex flex-col focus:outline-none" data-testid="ikr-choose-market-dialog">
      {/* Mock copy — final copy pending legal/marketing review (VRM-5346). */}
      <div className="gap-xs flex flex-col">
        <div className="flex items-start justify-between gap-[16px]">
          <span className="text-title-sm">Choose a Market</span>
          <Button size="icon" onClick={() => toggleDialog(false)} variant="secondary" aria-label="Close">
            <Close />
          </Button>
        </div>
        <span className="text-2xs text-secondary-foreground">
          {/* Before a row is picked this quotes the first preview, which is the Market with the
              largest allocation and so the most room. A lower-capacity Market takes less and
              leaves the remainder deposited, so until the choice is made the figure is a ceiling
              rather than a promise — said with the same `up to` the breakdown rows below use. */}
          {selectedRow ? '' : 'Up to '}
          {formatAmount(inKindPositionAssets)} will be withdrawn as a lending position in the Market
          you choose.{' '}
          <ExternalLink href={IN_KIND_REDEMPTION_HELP_URL} underlined>
            Learn more
            <ArrowRight className="ml-[2px] inline size-3 align-middle" />
          </ExternalLink>
        </span>
      </div>
      <SectionErrorBoundary fallback={<ErrorServiceUnavailable minHeight="360px" />}>
        <div className="gap-md flex flex-col">
          {selectionAnnouncement && <span role="status" aria-live="polite" className="sr-only">
              {selectionAnnouncement}
            </span>}
          {!isLoading && rows.length > 0 &&
        // 12px inset and 8px between rows, per the Figma "Card / Lend" node. `padding`
        // only offers 20px or 32px, so the inset is set as a class instead.
        <Card className="gap-xs bg-muted p-sm flex-col shadow-none" padding="none">
              <div className="gap-sm grid grid-cols-[minmax(0,1fr)_auto] items-center">
                <div className="gap-2xs flex min-w-0 items-center">
                  <span className="text-2xs text-secondary-foreground">To your wallet</span>
                  <InfoTooltip tooltipContent={<>
                        {WALLET_LEG_NOTE} {WALLET_LEG_CEILING_NOTE}
                      </>} />
                </div>
                <span className="text-foreground text-2xs text-right whitespace-nowrap" data-testid="ikr-breakdown-wallet">
                  {walletAssets > ZERO ? `up to ${formatAmount(walletAssets)}` : formatAmount(ZERO)}
                </span>
              </div>
              <div className="gap-sm grid grid-cols-[minmax(0,1fr)_auto] items-center">
                <div className="gap-2xs flex min-w-0 items-center">
                  <span className="text-2xs text-secondary-foreground">
                    {selectedMarketName ? `As a ${selectedMarketName} position` : 'Choose a Market'}
                  </span>
                  <InfoTooltip tooltipContent={selectedRow && selectedMarketName && vaultAsset && isDefined(vaultData) ? <InKindRedemptionPositionExplanation vaultAsset={vaultAsset} marketName={selectedMarketName} withdrawnAssets={selectedRow.preview.exitAssets} penaltyAssets={selectedRow.preview.feeAssets} marketAssets={selectedRow.preview.netAssets} idleAssets={selectedRow.preview.idleAssets} penaltyRate={penalty} /> :
              // No Market picked yet, so there are no amounts to break down.
              <>
                          Supplied to the Market as a lending position, after the redemption fee.
                          Withdrawing it later depends on that Market&apos;s available liquidity.
                        </>} />
                </div>
                <span className="text-foreground text-2xs text-right whitespace-nowrap" data-testid="ikr-breakdown-market">
                  {selectedRow ? formatAmount(selectedRow.preview.netAssets) : '--'}
                </span>
              </div>
            </Card>}
          {isLoading ? <div className="gap-xs flex flex-col">
              {Array.from({
            length: 3
          }).map((_, i) => <Skeleton key={i} className="h-[64px] rounded-lg" />)}
            </div> : (marketsError || isDefined(snapshotError) || isMarketsPartial) && rows.length === 0 ?
        // The vault snapshot failing leaves `previews` empty with no markets error of its
        // own — without this branch an RPC outage reads as "no Markets are available",
        // a property of the vault rather than of the service.
        <ErrorServiceUnavailable minHeight="120px" /> : rows.length === 0 ? <CardText className="bg-muted h-[120px] shadow-none" text="No Markets are available for in-kind redemption." /> : <div className="gap-xs flex flex-col">
              <div className={cn(ROW_GRID, 'px-sm')}>
                <span className="text-2xs text-secondary-foreground">Market</span>
                <div className="gap-2xs flex items-center justify-end md:justify-start">
                  {/* Vault allocation, not market liquidity — "Liquidity" would misname it. */}
                  <span className="text-2xs text-secondary-foreground">
                    Capacity{vaultAsset?.symbol ? ` (${vaultAsset.symbol})` : ''}
                  </span>
                  <InfoTooltip tooltipContent={<>
                        The most you can redeem through this Market, before the redemption fee. Your
                        exit can be larger: the Vault pays what its available liquidity covers, and
                        this Market funds the rest. It is not the Market&apos;s own liquidity, which
                        is what later withdrawals from your new position will depend on.
                      </>} />
                </div>
                <span className="text-2xs text-secondary-foreground hidden md:block">APY</span>
              </div>
              <div role="radiogroup" aria-label="Market" className="gap-xs flex flex-col">
                {rows.map((row_3, index_2) => {
              const {
                preview: preview_3
              } = row_3;
              const key = previewKey(preview_3);
              const isDisabled = isRowDisabled(row_3);
              const isSelected = key === selectedKey && !isDisabled;
              const rowMarketName = getMarketName({
                collateralAsset: row_3.collateralAsset,
                loanAsset: row_3.loanAsset
              });
              const {
                loanSymbol,
                collateralSymbol
              } = getMarketNameParts({
                collateralAsset: row_3.collateralAsset,
                loanAsset: row_3.loanAsset,
                lltv: preview_3.marketParams.lltv
              });
              const lltvTag = <Tag className="shrink-0">
                      {formatters.percent.readable().trailingZero(false).digits(0).unit('%').of(preview_3.marketParams.lltv, 18)}
                    </Tag>;
              // The visible cells read as bare values ("86% 1,204 USDC 4.2%") to a screen
              // reader — the column headings are unrelated elements. Name each field.
              const rowLabel = [rowMarketName, `LLTV ${formatters.percent.readable().trailingZero(false).digits(0).unit('%').of(preview_3.marketParams.lltv, 18)}`, `capacity ${formatAmount(row_3.capacityAssets)}`, isDefined(row_3.supplyApy) ? `APY ${formatters.percent.digits(2).unit('%').of(row_3.supplyApy, 18)}` : undefined].filter(isDefined).join(', ');
              return (
                // Not a <button>: Trusted by nests interactive popovers (no button-in-button).
                <div key={key} ref={element => {
                  rowRefs.current[index_2] = element;
                }} role="radio"
                // Roving tabindex: one stop for the whole group, as a radiogroup expects.
                tabIndex={index_2 === tabbableIndex ? 0 : -1} aria-checked={isSelected} aria-disabled={isDisabled || undefined} aria-label={rowLabel}
                // The label replaces descendant text, which is where the visible disabled
                // reason lives — point back at it so a screen reader hears why the row
                // cannot be picked, not just that it is dimmed.
                aria-describedby={isDisabled ? `ikr-row-reason-${index_2}` : undefined} onClick={() => {
                  if (!isDisabled) setSelectedKey(key);
                }} onKeyDown={event => {
                  if (rows.length === 0) return;
                  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                    event.preventDefault();
                    selectRowFrom(index_2, 1);
                  } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                    event.preventDefault();
                    selectRowFrom(index_2, -1);
                  } else if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectRowAt(index_2);
                  }
                }} className={cn(ROW_GRID, 'bg-muted px-sm py-md rounded-lg text-left transition-colors', isDisabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent cursor-pointer',
                // Keyboard focus reuses the selected ring instead of stacking the UA
                // outline on top of it. Arrow keys move focus and selection together, so
                // the two coincide; tabbing in with nothing selected still shows a ring.
                'focus-visible:inset-ring-primary focus-visible:inset-ring-1 focus-visible:outline-none', isSelected && 'inset-ring-primary inset-ring-1')} data-testid="ikr-market-row">
                      <div className="gap-2xs flex min-w-0 items-center">
                        {/* Renders unselected too: the affordance has to read as a control
                            before the first click, not only after it. */}
                        <span aria-hidden className={cn('flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors', isSelected ? 'border-primary' : 'border-muted-foreground')} data-testid="ikr-market-radio">
                          {isSelected && <span className="bg-primary size-2 rounded-full" />}
                        </span>
                        <span className="gap-2xs inline-flex min-w-0 items-center">
                          <Avatar aria-hidden src={row_3.loanAsset.logoURI} size="small" />
                          <TruncatedTooltip text={loanSymbol}>
                            {ref => <span ref={ref} tabIndex={-1} className="text-foreground min-w-0 truncate text-xs">
                                {loanSymbol}
                              </span>}
                          </TruncatedTooltip>
                        </span>
                        {row_3.collateralAsset ? <>
                            <span aria-hidden className="bg-muted-foreground h-sm w-px shrink-0 rounded-full" />
                            <span className="sr-only">backed by</span>
                            <span className="gap-2xs inline-flex min-w-0 items-center">
                              <Avatar aria-hidden src={row_3.collateralAsset.logoURI} size="small" />
                              <TruncatedTooltip text={collateralSymbol ?? ''}>
                                {ref_0 => <span ref={ref_0} tabIndex={-1} className="text-foreground min-w-0 truncate text-xs">
                                    {collateralSymbol}
                                  </span>}
                              </TruncatedTooltip>
                              {lltvTag}
                            </span>
                          </> : lltvTag}
                      </div>
                      {/* Same formatter as every other amount here: `formatters.short` rounded
                          capacity to "1.2K" beside a breakdown reading "1,204.37 USDC", two
                          numbers for one quantity. */}
                      <span className="text-foreground text-right text-xs md:text-left">
                        {formatAmount(row_3.capacityAssets)}
                      </span>
                      <span className="text-foreground hidden text-xs md:block" data-testid="ikr-market-row-apy">
                        {isDefined(row_3.supplyApy) ? formatters.percent.digits(2).unit('%').of(row_3.supplyApy, 18) : '-'}
                      </span>
                      {isDefined(row_3.disabledReason) && <span id={`ikr-row-reason-${index_2}`} className="text-2xs text-secondary-foreground col-span-full" data-testid="ikr-market-row-disabled-reason">
                          {row_3.disabledReason}
                        </span>}
                    </div>
              );
            })}
              </div>
            </div>}
          {!selectedRow && noMarketTakesFullAmount && <InfoCallout className="rounded-lg" data-testid="ikr-no-full-capacity-callout">
              No single Market can take {formatAmount(requestedExitAssets)} right now. The Market
              you choose takes what it can and the rest stays in your Vault.
            </InfoCallout>}
          {remainderStayingInVault > ZERO && <InfoCallout className="rounded-lg" data-testid="ikr-partial-redemption-callout"
        // Selection can silently shrink the amount — announce for screen readers.
        role="status" aria-live="polite">
              Only {formatAmount(effectiveExitAssets)} can be redeemed through this Market now. The
              remaining {formatAmount(remainderStayingInVault)} stays deposited in your Vault.
            </InfoCallout>}
          {isMarketsPartial && rows.length > 0 && <p className="text-secondary-foreground px-sm text-xs" data-testid="ikr-markets-partial-notice">
              Some Markets could not be loaded, so this list may be incomplete.
            </p>}
          {isDefined(snapshotError) && rows.length > 0 &&
        // A later 10s vault poll failing keeps the previous rows on screen
        // (`keepPreviousData`), so their capacities are as stale as the failure is old.
        // Review is gated below for the same reason — pressing on would only move the
        // failure to the review's generic preparation error.
        <InfoCallout role="alert">
              The Vault could not be refreshed, so these numbers may be out of date. Retrying
              automatically.
            </InfoCallout>}
          <Button size="lg" disabled={!selectedRow || isDefined(snapshotError)} onClick={openReview} data-testid="ikr-review-button">
            Review
          </Button>
        </div>
      </SectionErrorBoundary>
    </div>;
};
export default VaultV2InKindRedemptionDialog;